import { useControls, button, folder } from "leva";
import { useFilterSpices } from "@/hooks/useFilter";
import { useSpices } from "@/hooks/useSpices";
import { useCallback, useState } from "react";
import { List } from "./list/List";
import { Controls } from "./Controls";
import { Toolbar } from "./toolbar/Toolbar";
import { Sheet } from "@/layouts/Sheet";
import NutritionFacts from "@/components/nutrition/NutritionFacts";
import { Header, EmptySpicesState } from "@/components/nutrition";
import { useUiStore } from "@/stores/useUiStore";
import { ChevronRight, ChartPie } from "lucide-react";
import type { UiProps } from "@/ui";
import { View } from "@/layouts/views/View";
import { DesktopView } from "@/layouts/views/DesktopView";
import { DesktopViewToggleButton } from "@/layouts/views/DesktopViewToggleButton";

export function PreShake(props: UiProps) {
  const { addSpice, addNew, increment, decrement } = useSpices();
  const [spices] = useFilterSpices();
  const [selectedSpice, setSelectedSpice] = useState<number | null>(null);
  const [drawerState, setDrawerState] = useState<"closed" | "spices" | "info">("closed");
  const [isNutritionOpen, setIsNutritionOpen] = useState<boolean>(false);

  const setPhase = useUiStore((s) => s.setPhase);

  const handleShake = useCallback(async () => {
    if (!spices.size) return alert("no spices")
    setPhase('shaking');
    props.ani.shake?.invalidate().playPause(props.ani.shake?.duration() * 0.6);
  }, [props.ani.shake, spices, setPhase]);

  useControls(
    "UI",
    {
      preShake: folder(
        {
          "+": button(() =>
            selectedSpice
              ? increment(selectedSpice)
              : alert("no spices selected")
          ),
          "-": button(() =>
            selectedSpice
              ? decrement(selectedSpice)
              : alert("no spices selected")
          ),
          "Shake it up": button(() => handleShake()),
        },
        { collapsed: false }
      ),
    },
    [spices, selectedSpice, addSpice, increment, decrement, handleShake]
  );

  return (
    <>

      {/* Mobile View - renders only on mobile */}
      <View className="preshake">

        {/* Expandable Sheet for mobile */}
        <Sheet
          className="lg:hidden"
          state={drawerState}
          onClose={() => setDrawerState("closed")}
        >
          {drawerState === "spices" && (
            <List
              selectedSpice={selectedSpice}
              setSelectedSpice={(id) => {
                if (id !== null && !spices.has(id)) addNew(id);
                setSelectedSpice(id);
              }}
              className="flex-1 min-h-0"
            />
          )}
          {drawerState === "info" && spices.size > 0 && <NutritionFacts />}
          {drawerState === "info" && spices.size === 0 && <EmptySpicesState />}
        </Sheet>

        <Toolbar
          className="pointer-events-auto w-full pt-3 px-4 pb-2"
          selectedSpice={selectedSpice}
          onSelectSpice={setSelectedSpice}
          onOpenList={() => setDrawerState(drawerState === "spices" ? "closed" : "spices")}
          isListOpen={drawerState === "spices"}
          spiceCount={spices.size}
          onShowInfo={() => setDrawerState(drawerState === "info" ? "closed" : "info")}
          showInfo={drawerState === "info"}
        />

        <Controls
          className="pointer-events-auto w-full px-4 pb-4"
          selectedSpice={selectedSpice}
          onShake={handleShake}
        />

      </View>

      {/* Desktop View - renders only on desktop */}
      <DesktopView className="preshake pt-2 bg-main">

        <List
          className="flex-1 min-h-0 pointer-events-auto"
          selectedSpice={selectedSpice}
          setSelectedSpice={setSelectedSpice}
        />

        <div className="flex flex-col py-2">
          <Toolbar
            className="pointer-events-auto w-full lg:bg-primary lg:px-4 lg:pb-0 px-4 pb-2"
            selectedSpice={selectedSpice}
            onSelectSpice={setSelectedSpice}
            onOpenList={() => {}}
            isListOpen={false}
            spiceCount={spices.size}
          />

          <Controls
            className="pointer-events-auto w-full px-4"
            selectedSpice={selectedSpice}
            onShake={handleShake}
          />
        </div>

      </DesktopView>

      {/* Floating icon to toggle nutrition sidebar when closed */}
      <DesktopViewToggleButton isOpen={isNutritionOpen} onClick={() => setIsNutritionOpen(true)}>
        <ChartPie size={20} />
      </DesktopViewToggleButton>

      {/* Nutrition sidebar on desktop */}
      <DesktopView position="right" isOpen={isNutritionOpen} className="nutrition h-dvh w-full border-l pointer-events-auto bg-main overflow-y-auto py-0 p-0">

        <div onClick={() => setIsNutritionOpen(!isNutritionOpen)} className="flex w-full items-center justify-between bg-secondary text-main p-4 hover:cursor-pointer">
          <Header title="Nutrition Facts" className="text-main"/>
          <button onClick={() => setIsNutritionOpen(!isNutritionOpen)} className="p-2 rounded transition-opacity hover:opacity-70">
            <ChevronRight size={20} className="text-main" />
          </button>
        </div>
        <NutritionFacts gapClass="gap-8" className="p-4" />
      </DesktopView>
    </>
  );
}
